/**
 * giris.tsx
 *
 * NE İŞE YARAR : Giriş ekranı — `AU-01`i çağırır, sonuca göre oturum açar ya da 2FA'ya geçer
 * KAYNAK SPEC  : 15-arayuz-standardi.md §15 (giriş ekranı) · §15.1 (kurallar)
 *                04-api-sozlesmesi.md AU-01 · ADR-019 §1 (iki kimlik kaynağı)
 *                ADR-008 §8 (hesap sayımı engellenir) · `15` §10 (form kuralları)
 * BAĞLI OLDUĞU : src/api/istemci.ts · src/kimlik/saglayici.ts · src/oturum/*
 * KULLANAN     : src/uygulama.tsx — `/giris` yolu
 * DİKKAT       : 1) TEK ALAN, İKİ KİMLİK KAYNAĞI (ADR-019 §1). `@` içeren bir değer
 *                   e-postadır ve **kimlik sağlayıcısından** geçer; içermeyen değer
 *                   telefondur ve doğrudan `AU-01`e gider. İki ayrı sekme yapılsaydı
 *                   kullanıcı her girişte hangisi olduğunu seçmek zorunda kalırdı;
 *                   hesabının hangi kaynakta olduğunu bilmesi gerekmiyor.
 *                2) TELEFON BURADA NORMALLEŞTİRİLMEZ. Sunucu `telefonuNormallestir` ile
 *                   kendi normalleştirmesini yapıyor (`src/kimlik/parmak-izi.ts`); istemci
 *                   de yapsaydı iki normalleştirme bir gün ayrışır ve kullanıcı sunucuda
 *                   var olan bir numarayla giremezdi.
 *                3) HATA AYRIMI YAPILMAZ (ADR-008 §8). Var olmayan hesap, yanlış parola,
 *                   devre dışı hesap ve kilitli hesap **aynı `401`**i döndürür; ekran da
 *                   tek bir mesaj gösterir. Sağlayıcının kendi hata metni de gösterilmez
 *                   (`saglayici.ts` notu 4) — orada ayrım vardır ve buraya sızarsa
 *                   sunucunun özenle kapattığı ayrım açılır.
 *                4) SUNUCUNUN `AUTH_REQUIRED` MESAJI BURADA EZİLİR. Genel metni
 *                   *"Oturum açmanız gerekiyor."*tur ve zaten oturum açmaya çalışan bir
 *                   kullanıcıya hiçbir şey söylemez. `hata.ts` notu 2'nin tarif ettiği
 *                   istisna budur ve tek örneğidir.
 *                5) CİHAZ SIRRI VARSA GÖNDERİLİR (`04` AU-01). Geçersizse sunucu hata
 *                   döndürmez, 2FA akışına düşürür — bu yüzden sır burada denetlenmez.
 *                6) TANINMAMA AÇIKLAMASI EKRANDA DURUR (`15` §15.1). Tarayıcı verisi
 *                   temizlendiğinde cihaz tanınmaz ve yeniden kod istenir; ADR-019 §5.1
 *                   bunu *"yöntemin doğası"* sayıyor ve kullanıcının bunu destek hattını
 *                   aramadan önce okuması gerekiyor.
 *                7) GÖNDERİRKEN BUTON PASİFTİR (`15` §10). Çift gönderim, iki ayrı
 *                   `mfa_challenge` üretir ve kullanıcıya iki SMS gider; ikinci kod
 *                   birincisini geçersiz kılar ve kullanıcı elindeki koda güvenemez.
 * SON GÜNCELLEME: 2026-08-31 12:40:00
 */
import { Anchor, Button, Divider, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiHatasi } from '../../api/hata';
import { istek } from '../../api/istemci';
import { epostaIleBelirtecAl, hazirMi } from '../../kimlik/saglayici';
import { metin } from '../../metin';
import { cihazSirriniOku } from '../../oturum/depo';
import { useOturum } from '../../oturum/baglam';
import type { HedefDurumu } from '../../oturum/koruma';
import { HataUyarisi } from '../ortak/hata-uyarisi';
import { KimlikDuzeni } from './duzen';

/** `04` AU-01'in iki yanıt biçimi. */
interface GirisYaniti {
  readonly mfa_required?: boolean;
  readonly session?: { readonly token: string; readonly expires_at: string };
  readonly challenge_id?: string;
  readonly phone_masked?: string;
}

/** Doğrulama ekranına taşınan durum — yol parametresi değil, gezinti durumu. */
export interface DogrulamaDurumu {
  readonly challengeId: string;
  readonly telefonMaskeli: string;
  /** Doğrulama bittikten sonra dönülecek yol — giriş ekranından taşınır. */
  readonly donulecekYol: string;
}

export function GirisEkrani(): ReactElement {
  const { oturumuBaslat } = useOturum();
  const yonlendir = useNavigate();
  const konum = useLocation();

  /**
   * Giriş sonrası dönülecek yol (`koruma.tsx` notu 3).
   *
   * Kimlik ekranlarının kendisi hedef olamaz: oradan gelinmişse kullanıcı giriş
   * yaptıktan sonra yeniden giriş ekranına atılırdı.
   */
  const hedef = (konum.state as HedefDurumu | null)?.hedef ?? '/';
  const donulecekYol = hedef.startsWith('/giris') ? '/' : hedef;

  const [kimlik, setKimlik] = useState('');
  const [parola, setParola] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hataMesaji, setHataMesaji] = useState<string | null>(null);

  const epostaMi = kimlik.includes('@');

  async function gonder(olay: FormEvent<HTMLFormElement>): Promise<void> {
    olay.preventDefault();
    if (gonderiliyor) {
      return;
    }

    setHataMesaji(null);
    setGonderiliyor(true);

    try {
      const govde = await girisGovdesi(kimlik.trim(), parola, epostaMi);
      if (govde === null) {
        // Sağlayıcı doğrulayamadı — sebebi taşınmaz (başlıktaki not 3).
        setHataMesaji(hazirMi() ? metin('giris.reddedildi') : metin('giris.saglayiciYok'));
        return;
      }

      const yanit = await istek<GirisYaniti>({
        yol: '/auth/session',
        yontem: 'POST',
        govde,
      });

      if (yanit.mfa_required === false && yanit.session !== undefined) {
        // Tanınan cihaz: 2FA sorulmadan oturum açıldı (`04` AU-01 akış adım 3).
        await oturumuBaslat(yanit.session.token, yanit.session.expires_at);
        // `void` — yönlendirici bir `Promise` döndürür; beklenecek bir sonucu yoktur.
        void yonlendir(donulecekYol, { replace: true });
        return;
      }

      if (yanit.challenge_id === undefined) {
        // Sözleşmeye uymayan yanıt: uydurmak yerine genel hata gösterilir.
        setHataMesaji(metin('hata.beklenmeyen'));
        return;
      }

      const durum: DogrulamaDurumu = {
        challengeId: yanit.challenge_id,
        telefonMaskeli: yanit.phone_masked ?? '',
        donulecekYol,
      };
      void yonlendir('/giris/dogrulama', { state: durum, replace: true });
    } catch (hata) {
      setHataMesaji(girisHatasi(hata));
    } finally {
      setGonderiliyor(false);
    }
  }

  return (
    <KimlikDuzeni
      baslik={metin('giris.baslik')}
      aciklama={metin('giris.aciklama')}
      altBilgi={
        <Text size="xs" c="notr.6">
          {metin('giris.tanimaNotu')}
        </Text>
      }
    >
      <form onSubmit={(olay) => void gonder(olay)} noValidate>
        <Stack gap="md">
          <HataUyarisi mesaj={hataMesaji} />

          <TextInput
            label={metin('giris.kimlik')}
            description={metin('giris.kimlikIpucu')}
            value={kimlik}
            onChange={(olay) => setKimlik(olay.currentTarget.value)}
            autoComplete="username"
            required
            // Yer tutucu etiket yerine geçmez (`15` §13) — bu yüzden yok.
          />

          <PasswordInput
            label={metin('giris.parola')}
            value={parola}
            onChange={(olay) => setParola(olay.currentTarget.value)}
            autoComplete="current-password"
            required
          />

          {/* Başlıktaki not 7 — gönderirken pasif, çift gönderim engellenir. */}
          <Button type="submit" loading={gonderiliyor} fullWidth>
            {metin('giris.gonder')}
          </Button>

          <Group justify="center">
            <Anchor component={Link} to="/parola/sifirlama-talebi" size="sm">
              {metin('giris.parolamiUnuttum')}
            </Anchor>
          </Group>

          {/*
            Google ve Apple ile giriş düğmeleri BİLEREK YOKTUR. İkisi de kimlik
            sağlayıcısının açılır pencere akışını gerektiriyor ve o akış henüz
            yazılmadı (`saglayici.ts` notu 2). Çalışmayan bir düğme koymak,
            çalıştığını söylemektir. Sağlayıcı hiç yapılandırılmamışsa e-posta yolu da
            kapalıdır ve ekran bunu **açıkça** yazar — sessizce reddetmek, kullanıcıya
            kendi parolasını yanlış hatırlıyor sandırırdı.
          */}
          {hazirMi() ? null : (
            <>
              <Divider label={metin('giris.saglayiciAyrac')} labelPosition="center" />
              <Text size="sm" c="notr.7" ta="center">
                {metin('giris.saglayiciYok')}
              </Text>
            </>
          )}
        </Stack>
      </form>
    </KimlikDuzeni>
  );
}

/**
 * `AU-01` gövdesini kurar.
 *
 * E-posta yolunda önce kimlik sağlayıcısı doğrular; başarısızsa `null` döner ve istek
 * hiç atılmaz. Telefon yolunda gövde doğrudan kurulur (başlıktaki not 2).
 */
async function girisGovdesi(
  kimlik: string,
  parola: string,
  epostaMi: boolean,
): Promise<Record<string, unknown> | null> {
  const cihazSirri = cihazSirriniOku();
  const cihaz = cihazSirri === null ? {} : { device_secret: cihazSirri };

  if (!epostaMi) {
    return { phone: kimlik, password: parola, ...cihaz };
  }

  const belirtec = await epostaIleBelirtecAl(kimlik, parola);
  return belirtec === null ? null : { firebase_id_token: belirtec, ...cihaz };
}

/** `04` AU-01'in üç hata kodunu ekranın diline çevirir (başlıktaki notlar 3–4). */
function girisHatasi(hata: unknown): string {
  if (!(hata instanceof ApiHatasi)) {
    return metin('hata.beklenmeyen');
  }
  switch (hata.kod) {
    case 'AUTH_REQUIRED':
    case 'MALFORMED_REQUEST':
      return metin('giris.reddedildi');
    case 'RATE_LIMITED':
      return metin('giris.cokFazlaDeneme');
    case 'UPSTREAM_UNAVAILABLE':
      return metin('giris.smsGonderilemedi');
    default:
      // Diğer her şeyde sunucunun kendi mesajı gösterilir (`15` §9.1).
      return hata.message;
  }
}
